import { useState } from 'react'
import { api } from '../lib/api'
import { Loader2, ExternalLink, Check } from 'lucide-react'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import Modal from './Modal'

interface AddCompetitorModalProps {
  brandId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function AddCompetitorModal({ brandId, isOpen, onClose, onSuccess }: AddCompetitorModalProps) {
  const [step, setStep] = useState<'input' | 'review'>('input')
  const [domain, setDomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Extracted brand info
  const [extractedInfo, setExtractedInfo] = useState<{
    name: string
    domain: string
    category: string
    description: string
  } | null>(null)

  // Editable fields for review
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')

  const handleClose = () => {
    setStep('input')
    setDomain('')
    setExtractedInfo(null)
    setName('')
    setCategory('')
    setDescription('')
    setError(null)
    onClose()
  }

  const handleExtractInfo = async () => {
    if (!domain.trim()) {
      setError('Please enter a domain')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.post(`/competitors/${brandId}/extract-info`, {
        domain: domain.trim()
      })

      if (response.data.success) {
        setExtractedInfo(response.data)
        setName(response.data.name)
        setCategory(response.data.category)
        setDescription(response.data.description)
        setStep('review')
      } else {
        setError(response.data.error || 'Failed to extract brand information')
      }
    } catch (err: any) {
      console.error('Extract error:', err)
      setError(err.response?.data?.message || 'Failed to extract brand information. Please check the domain and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)

    try {
      const response = await api.post(`/competitors/${brandId}`, {
        name: name.trim(),
        domain: extractedInfo!.domain,
        category: category.trim(),
        description: description.trim()
      })

      if (response.data.success) {
        // Track competitor added
        trackEvent(ANALYTICS_EVENTS.COMPETITOR_ADDED, {
          competitorDomain: extractedInfo!.domain
        })

        onSuccess()
        handleClose()
      } else {
        setError(response.data.error || 'Failed to add competitor')
      }
    } catch (err: any) {
      console.error('Submit error:', err)
      setError(err.response?.data?.message || 'Failed to add competitor')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" closeOnBackdrop={!loading && !submitting}>
      <Modal.Header onClose={handleClose} showCloseButton={!loading && !submitting}>
        <h2 className="text-2xl font-bold text-gray-900">Add Competitor</h2>
      </Modal.Header>
      <Modal.Body>
          {step === 'input' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter the competitor's website domain and we'll automatically extract their brand information.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competitor Website Domain
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleExtractInfo()}
                      placeholder="example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={loading}
                    />
                    {loading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Example: nike.com, coca-cola.com
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtractInfo}
                  disabled={loading || !domain.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Extracting...</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      <span>Extract Info</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'review' && extractedInfo && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Information Extracted</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Review and edit the information below, then click "Add Competitor" to save.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain
                </label>
                <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                  {extractedInfo.domain}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry / Category
                </label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4">
                <button
                  onClick={() => setStep('input')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Back
                </button>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !name.trim() || !category.trim() || !description.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Add Competitor</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
      </Modal.Body>
    </Modal>
  )
}
